import React, { useMemo, useState } from 'react';
import { Sun, Moon, AlertCircle, Lightbulb, ArrowRight, Mail, Clock, Check } from 'lucide-react';

/**
 * HiddenInsights - AI-powered insights with email digest scheduling
 * Includes: Activity patterns, knowledge gaps, recommendations, email digest
 */
const HiddenInsights = ({ analytics, dailyActivity = [], userId, chatbotId, userEmail }) => {
    const [showDigestForm, setShowDigestForm] = useState(false);
    const [digestSettings, setDigestSettings] = useState({
        frequency: 'weekly',
        dayOfWeek: 1, // Monday
        hourOfDay: 9
    });
    const [digestSaved, setDigestSaved] = useState(false);

    const peakAnalysis = useMemo(() => {
        if (!dailyActivity || dailyActivity.length === 0) return null;
        const sorted = [...dailyActivity].sort((a, b) => b.count - a.count);
        const peakDay = sorted[0];
        const quietDay = sorted[sorted.length - 1];
        return {
            peakDay: peakDay ? new Date(peakDay.day).toLocaleDateString('en-US', { weekday: 'long' }) : null,
            peakCount: peakDay?.count || 0,
            quietDay: quietDay ? new Date(quietDay.day).toLocaleDateString('en-US', { weekday: 'long' }) : null,
        };
    }, [dailyActivity]);

    const knowledgeGaps = useMemo(() => {
        const topics = analytics?.topTopics || [];
        return topics.slice(0, 2).map(topic => ({
            topic: topic.phrase,
            queries: topic.count,
            confidence: 'low'
        }));
    }, [analytics]);

    const recommendations = [
        { id: 1, priority: 'high', title: 'Add training for top topic', description: `"${analytics?.topTopics?.[0]?.phrase || 'Assessment'}" needs coverage` },
        { id: 2, priority: 'medium', title: 'Enable after-hours auto-reply', description: `Low activity on ${peakAnalysis?.quietDay || 'weekends'}` }
    ];

    const handleSaveDigest = async () => {
        try {
            const response = await fetch('/api/digests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    chatbotId,
                    email: userEmail,
                    frequency: digestSettings.frequency,
                    dayOfWeek: digestSettings.dayOfWeek,
                    hourOfDay: digestSettings.hourOfDay
                })
            });

            if (response.ok) {
                setDigestSaved(true);
                setTimeout(() => {
                    setShowDigestForm(false);
                    setDigestSaved(false);
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to save digest:', error);
        }
    };

    const dayOptions = [
        { value: 0, label: 'Sunday' },
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' }
    ];

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            {/* Header */}
            <div className="mb-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Lightbulb size={12} className="text-amber-500" />
                    Insights
                </h3>
            </div>

            {/* Peak Hours */}
            {peakAnalysis && (
                <div className="mb-5 pb-5 border-b border-slate-100">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-3">Activity Pattern</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                            <Sun size={14} className="text-amber-500" />
                            <div>
                                <div className="text-sm font-medium text-slate-700">{peakAnalysis.peakDay}</div>
                                <div className="text-[10px] text-slate-400">Peak day</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Moon size={14} className="text-indigo-400" />
                            <div>
                                <div className="text-sm font-medium text-slate-700">{peakAnalysis.quietDay}</div>
                                <div className="text-[10px] text-slate-400">Quietest</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Knowledge Gaps */}
            {knowledgeGaps.length > 0 && (
                <div className="mb-5 pb-5 border-b border-slate-100">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <AlertCircle size={10} className="text-amber-500" />
                        Knowledge Gaps
                    </div>
                    <div className="space-y-2">
                        {knowledgeGaps.map((gap, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600 truncate max-w-[140px]">{gap.topic}</span>
                                <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-600">
                                    {gap.confidence}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recommendations */}
            <div className="mb-5 pb-5 border-b border-slate-100">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Recommendations</div>
                <div className="space-y-2">
                    {recommendations.map((rec) => (
                        <div key={rec.id} className="p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors group cursor-pointer">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${rec.priority === 'high' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'
                                            }`}>
                                            {rec.priority}
                                        </span>
                                    </div>
                                    <p className="text-xs font-medium text-slate-700">{rec.title}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{rec.description}</p>
                                </div>
                                <ArrowRight size={12} className="text-slate-300 group-hover:text-primary transition-colors mt-1" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Email Digest */}
            <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Mail size={10} className="text-violet-500" />
                    Email Digest
                </div>

                {!showDigestForm ? (
                    <button
                        onClick={() => setShowDigestForm(true)}
                        className="w-full p-3 rounded-lg border border-dashed border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-colors text-xs text-slate-500 hover:text-violet-600 flex items-center justify-center gap-2"
                    >
                        <Clock size={12} />
                        Schedule weekly report
                    </button>
                ) : (
                    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                        {digestSaved ? (
                            <div className="flex items-center justify-center gap-2 py-2 text-emerald-600 text-xs font-medium">
                                <Check size={14} />
                                Digest scheduled!
                            </div>
                        ) : (
                            <>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-[10px] text-slate-500 block mb-1">Frequency</label>
                                        <select
                                            value={digestSettings.frequency}
                                            onChange={(e) => setDigestSettings({ ...digestSettings, frequency: e.target.value })}
                                            className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </select>
                                    </div>

                                    {digestSettings.frequency === 'weekly' && (
                                        <div>
                                            <label className="text-[10px] text-slate-500 block mb-1">Day</label>
                                            <select
                                                value={digestSettings.dayOfWeek}
                                                onChange={(e) => setDigestSettings({ ...digestSettings, dayOfWeek: parseInt(e.target.value) })}
                                                className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
                                            >
                                                {dayOptions.map(day => (
                                                    <option key={day.value} value={day.value}>{day.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-[10px] text-slate-500 block mb-1">Time</label>
                                        <select
                                            value={digestSettings.hourOfDay}
                                            onChange={(e) => setDigestSettings({ ...digestSettings, hourOfDay: parseInt(e.target.value) })}
                                            className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
                                        >
                                            {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(hour => (
                                                <option key={hour} value={hour}>
                                                    {hour > 12 ? `${hour - 12}:00 PM` : hour === 12 ? '12:00 PM' : `${hour}:00 AM`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => setShowDigestForm(false)}
                                        className="flex-1 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveDigest}
                                        className="flex-1 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded hover:bg-violet-700 transition-colors"
                                    >
                                        Schedule
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HiddenInsights;
